/**
 * Rich Text Storage Utility using IndexedDB
 * Handles storing and retrieving rich text content for persistence across page refreshes
 */

const DB_NAME = "RichTextStorageDB";
const DB_VERSION = 1;
const STORE_NAME = "richTexts";

class RichTextStorage {
  constructor() {
    this.db = null;
    this.init();
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("elementId", "elementId", { unique: false });
        }
      };
    });
  }

  async storeRichText(elementId, content, shortDescription, categoryId, categoryName, categoryFieldName, categoryDescription) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const data = {
        id: elementId,
        elementId: elementId,
        content: content,
        shortDescription: shortDescription,
        categoryId: categoryId,
        categoryName: categoryName,
        categoryFieldName: categoryFieldName,
        categoryDescription: categoryDescription,
        timestamp: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error storing rich text:", request.error);
        reject(request.error);
      };
    });
  }

  async getRichText(elementId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(elementId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            content: result.content || "",
            shortDescription: result.shortDescription || "",
            categoryId: result.categoryId || "",
            categoryName: result.categoryName || "",
            categoryFieldName: result.categoryFieldName || "",
            categoryDescription: result.categoryDescription || "",
          });
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error(
          "IndexedDB: Error retrieving rich text:",
          request.error
        );
        reject(request.error);
      };
    });
  }

  async deleteRichText(elementId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(elementId);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error deleting rich text:", request.error);
        reject(request.error);
      };
    });
  }

  async clearAllRichTexts() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error("IndexedDB: Error clearing rich texts:", request.error);
        reject(request.error);
      };
    });
  }

  async getAllRichTexts() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error(
          "IndexedDB: Error getting all rich texts:",
          request.error
        );
        reject(request.error);
      };
    });
  }
}

// Create a singleton instance
const richTextStorage = new RichTextStorage();

export default richTextStorage;
